import type { Clave, Idioma } from './i18n/textos';

export type ViewState =
  | 'Dashboard'
  | 'Leads'
  | 'Clients'
  | 'Estimates'
  | 'Projects'
  | 'Finance'
  | 'Calendar'
  | 'Pricebook'
  | 'Templates'
  | 'ProposalViewer'
  | 'Settings'
  | 'DesignShowcase'
  | 'FollowUps'
  | 'TimeClock'
  | 'Services'
  | 'Workforce'
  | 'Inventory'
  | 'Maintenance'
  | 'Classes'
  | 'AIAssistant';

export type UserRole = 'Admin' | 'Office' | 'Foreman' | 'Laborer' | 'Maintenance';

export interface WorkspaceUser {
    id: string;
    userId: string;
    workspaceId: string;
    role: string;
    allowedViews: string[];
}

export interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  allowedViews: ViewState[];
  dateAdded: string;
  photoUrl?: string;
  clientId?: string;
}

export interface Crew {
  id: string;
  name: string;
  leaderId: string; // TeamMember ID (Foreman)
  memberIds: string[]; // TeamMember IDs (Laborers)
  color: string;
  isActive: boolean;
}

/**
 * El taller / oficina central como destino de fichaje (tabla `workspace_offices`,
 * una por taller). Vive en tabla propia y no como clave de `settings` porque
 * `anon_select_settings` deja leer settings a cualquiera con la llave publishable
 * — la dirección del taller no puede quedar pública.
 */
export interface WorkspaceOffice {
  id: string;
  workspaceId: string;
  /** Etiqueta que ve la cuadrilla al elegir destino ('Shop / Main Office' por defecto). */
  name: string;
  /** Al revés que en las obras, la dirección SÍ se guarda: sin ella nadie puede
   *  auditar qué geocerca está mal puesta. */
  address?: string;
  siteCoordinates?: { lat: number; lng: number };
  /** Radio de la geocerca en pies. El esquema lo acota entre 100 y 5280. */
  geofenceRadiusFt: number;
  /** Candado "un peón solo ficha en su obra asignada". Arranca APAGADO: hoy casi
   *  ninguna obra tiene gente asignada y encenderlo dejaría a la cuadrilla sin fichar. */
  enforceAssignment: boolean;
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface TimeLog {
  id: string;
  userId: string;       // auth.users.id (Supabase auth UUID)
  /** Canonical worker identity — same UUID as allowed_users.id. Server-stamped. */
  allowedUserId: string;
  userEmail?: string;   // Legacy fallback join key; prefer allowedUserId.
  userName: string;
  /** Snapshot of userName at clock-in time — survives upstream worker delete. */
  userNameSnapshot?: string;
  userRole: string;
  /** Agrupa los tramos de un mismo turno. Un turno es una o más filas con este
   *  mismo id, ordenadas por `segmentSeq`, de las cuales como mucho una sigue
   *  abierta. Las filas históricas son grupo de una. */
  shiftGroupId?: string;
  /** Orden del tramo dentro del turno, empezando en 1. */
  segmentSeq?: number;
  clockIn: string;
  clockInLocation?: { lat: number, lng: number };
  projectId?: string;
  /** @deprecated — use targetLabel. Kept for read compatibility during cleanup. */
  projectName?: string;
  /** service_contracts.id when the shift was on a recurring service route. */
  contractId?: string;
  /** workspace_offices.id cuando el tramo se fichó en el taller/oficina.
   *  Excluyente con projectId y contractId. */
  officeId?: string;
  /** DB-generated: 'project' | 'contract' | 'office' | 'general'. Read-only. */
  targetType?: 'project' | 'contract' | 'office' | 'general';
  /** Display label snapshot — survives upstream project/contract delete. */
  targetLabel?: string;
  photoUrl?: string;
  /** 'Closed' es un tramo ya cerrado que NO lo cerró el barrendero: la persona
   *  fichó salida o pasó su reloj a otro destino. 'Auto-closed' se reserva para
   *  el cierre automático, que es lo que los admins revisan. */
  status?: 'Working' | 'Lunch' | 'Auto-closed' | 'Closed';
  lunchStart?: string | null;
  lunchEnd?: string | null;
  clockOut?: string | null;
  /** Por qué se cerró el tramo: la persona fichó salida ('clock_out'), cambió de
   *  destino a media jornada ('transfer'), o lo cerró el barrendero ('auto'). */
  closedReason?: 'clock_out' | 'transfer' | 'auto';
  clockOutPhotoUrl?: string;
  locationOut?: { lat: number, lng: number };
  durationMinutes?: number | null;
  syncStatus?: 'synced' | 'pending' | 'failed';
  offlineTimestamp?: string;
  isOffSite?: boolean;
  distanceFromSiteFt?: number;
  /** Mirrors is_off_site at clock-out time (worker may have moved off-site mid-shift). */
  clockOutOffSite?: boolean;
  clockOutDistanceFromSiteFt?: number;
  breadcrumbs?: { lat: number; lng: number; timestamp: string; accuracy: number }[];
}

// --- NEW APPOINTMENT TYPE ---
export interface Appointment {
  id: string;
  clientId: string;
  clientName: string;
  clientAddress?: string;
  title: string;
  date: string; // ISO Date String YYYY-MM-DD
  startTime: string; // "09:00"
  endTime: string; // "10:00"
  type: 'Site Visit' | 'Service Call' | 'Consultation' | 'Final Walkthrough';
  notes?: string;
  assignedTo?: string[]; // Array of employee IDs
  durationMinutes?: number;
  /** Optional link to a project (P6 Schedule <-> Projects). */
  projectId?: string;
}

export interface OverheadItem {
  id: string;
  name: string;
  amount: number;
  frequency: 'Monthly' | 'Annual';
}

/** Equipment that contributes to overhead via depreciation + operating costs.
 *  Distinct from the fleet-tracking `Equipment` interface used for dispatch —
 *  this one lives inside BusinessSettings and only affects pricing math. */
export interface OverheadEquipment {
  id: string;
  name: string;
  purchasePrice: number;          // total acquisition cost
  usefulLifeYears: number;        // straight-line depreciation period
  fuelAnnualCost?: number;        // annualized
  maintenanceAnnualCost?: number; // annualized
  insuranceAnnualCost?: number;   // annualized
}

export interface Employee {
  id: string;
  /** Optional display name. The costing model is keyed by POSITION, not person —
   *  a row is "Laborer × 3", not three named people. Optional because new rows
   *  never set it; legacy rows keep theirs so nothing is lost on migration.
   *  Real people live in the Team/allowed_users table, never here. */
  name?: string;
  /** Human-readable position label. Auto-filled from `roleSlug` when the user
   *  picks from the dropdown; free text when they type their own. */
  role: string;
  /** Canonical role slug (see documents/categories/SPEC-v3-labor-roles.md).
   *  Identifica el puesto: alimenta el desplegable de posiciones y la detección
   *  de la fila del dueño. Ya no se une a ninguna tabla de tarifas — el costo
   *  por hora sale de esta misma fila (sueldo + carga + utilización) y del
   *  overhead del taller. */
  roleSlug?: string;
  /** How many people hold this position. One row can stand for a whole crew —
   *  wage, burden and billable hours are all multiplied by this. Defaults to 1
   *  when absent so every legacy row keeps its exact previous math. */
  headcount?: number;
  payType: 'Hourly' | 'Salary';
  wage: number;
  laborBurdenPercent: number;
  /** Legacy single-factor utilization. Kept on the type so old data deserializes
   *  cleanly. New code should populate availablePercent + billablePercent instead. */
  utilizationPercent?: number;
  /** % of season hours NOT lost to PTO/holidays/weather/training. */
  availablePercent?: number;
  /** % of available hours actually billed to a paying job. */
  billablePercent?: number;
  /** When false, this employee's cost rolls into overhead instead of the
   *  direct-labor rate. Use for office staff, owner draws, etc. */
  isBillable?: boolean;
  /* `weeksWorked` se retiró: la matemática lo leía, ninguna pantalla lo
   * escribía, y a un salariado le recortaba las horas sin recortarle el sueldo.
   * Ninguna fila del corpus lo tenía. Ver services/metrics.migration.test.ts. */
}

export interface BusinessProfile {
  businessName: string;
  currency: string;
  serviceArea: string;
  logoUrl?: string;
  email?: string;
  phone?: string;
  website?: string;
  industryType?: string;
  businessDescription?: string;
  /** US state code (e.g. "TX", "CA"). Drives SUTA + WC defaults in the
   *  Burden Builder. Lives inside the profile JSONB — no DB migration needed. */
  state?: string;
}

/** Burden Builder configuration. Replaces the single laborBurdenPercent guess
 *  with an itemized stack. The computed total is what flows into each
 *  employee's effective burden when `useBuilder` is true. */
export interface BurdenComposition {
  useBuilder: boolean;             // when false, employees use their typed burden % directly
  sutaOverride?: number;            // % override; otherwise pull from state defaults
  wcOverride?: number;              // % override; otherwise pull from state × trade defaults
  healthInsurancePerEmployeeMonthly?: number;  // $/employee/month
  retirementMatchPercent?: number;  // % of wage matched by employer
  otherBenefitsPercent?: number;    // catch-all for bonuses, PTO accrual, etc.
}

/* Aquí vivían `FollowUpConfig` y `AutomatedFollowUpSettings`: la configuración
 * de los correos automáticos de seguimiento y de petición de reseña. Esa
 * automatización se retiró —no hay proveedor de correo— junto con su procesador,
 * sus vistas SQL, su panel de ajustes y sus plantillas.
 *
 * La columna `settings.automated_follow_ups` sigue en la base con lo que se
 * hubiera guardado. No estorba y no la lee nadie; borrarla necesitaría una
 * migración y no gana nada. */

export interface Warranty {
  id: string;
  name: string;
  text: string;
}

/**
 * Datos bancarios para recibir transferencias (ACH). Se configuran una sola vez
 * en Estimados ▸ Propuesta ▸ Pagos y se copian a la propuesta cuando el
 * contratista prende "Transferencia bancaria" en la pestaña Compartir.
 *
 * Ese copiado es a propósito: la vista pública NO trae los datos bancarios del
 * negocio (a diferencia de Zelle, que sí viaja en `business`), así que la cuenta
 * solo sale en las propuestas donde se eligió cobrar por transferencia — no en
 * cualquiera que tenga un enlace válido.
 */
export interface BankTransferDetails {
  /** Titular tal como aparece en el banco. */
  accountHolderName?: string;
  bankName?: string;
  accountType?: 'checking' | 'savings';
  /** ABA de 9 dígitos. Se guarda solo el número, sin espacios ni guiones. */
  routingNumber?: string;
  accountNumber?: string;
  /** Opcional: "pon el número de propuesta en el memo", etc. */
  instructions?: string;
}

export interface BusinessSettings {
  profile: BusinessProfile;
  season: {
    weeksPerYear: number;
    daysPerWeek: number;
    hoursPerDay: number;
  };
  employees: Employee[];
  overhead: OverheadItem[];
  /** Equipment cost recovery (depreciation + operating costs). Optional —
   *  feeds into total overhead in services/metrics.ts. */
  equipment?: OverheadEquipment[];
  /** Burden Builder configuration. When `useBuilder` is true, individual
   *  employee burden % is overridden by the computed stack. */
  burden?: BurdenComposition;
  pricing: {
    targetMarginPercent: number;
    defaultMaterialMarkupPercent: number;
    taxLaborDefault: boolean;
  };
  rules: {
    minServiceCall: number;
    tripCharge: number;
    minHours: number;
    emergencySurchargePercent: number;
    /** Default day of the month service invoices are due, 1–31. A contract's
     *  own `paymentDueDay` overrides it. Optional so existing settings rows
     *  deserialize untouched; see DEFAULT_SERVICE_DUE_DAY for the fallback. */
    serviceInvoiceDueDay?: number;
  };
  /**
   * Los términos por defecto del espacio de trabajo, en inglés.
   *
   * Hubo un `defaultTermsPorIdioma` con una versión por idioma, escrita a mano.
   * Se quitó el 18-ago-2026 por dos razones, y la segunda es la que decide:
   *
   *   · Las propuestas se entregan en inglés y solo en inglés, así que no hay
   *     un segundo idioma que elegir.
   *   · La columna `default_terms_por_idioma` NUNCA existió en la base de
   *     datos. `toSnake` la mandaba igual, PostgREST rechazaba la fila ENTERA
   *     con PGRST204, y el resultado era que escribir un solo carácter en
   *     aquella caja rompía el guardado completo de Ajustes → Legal: términos,
   *     garantías y correo incluidos. Nunca se llegó a guardar ni a leer nada.
   */
  defaultTerms?: string;
  warranties?: Warranty[]; // NEW: Warranty library
  defaultEmailSubject?: string; // NEW: Default email config
  defaultEmailBody?: string;    // NEW: Default email config
  useHtmlEmail?: boolean;       // Toggle between plain text and HTML email
  defaultHtmlBody?: string;     // Raw HTML code from Canva or other design tools
  customTerms?: string;
  upsells?: ProposalUpsell[];
  showGallery?: boolean;
  galleryImages?: string[];
  showCompanyProfile?: boolean;
  companyProfileText?: string;
  showLineItemPricing?: boolean;
  showTotals?: boolean;
  coverImageTheme?: string;
  customCoverImageUrl?: string;
  accentColor?: string;
  fontFamily?: 'Sans' | 'Serif' | 'Mono';
  animationStyle?: 'None' | 'Fade' | 'Slide' | 'Zoom';
  paymentLink?: string;
  showZelle?: boolean;
  zelleEmail?: string;
  zellePhone?: string;
  /** Transferencia bancaria (ACH) a nivel negocio. Persiste en la columna
   *  `bank_transfer` (jsonb) de la tabla settings. */
  showBankTransfer?: boolean;
  bankTransfer?: BankTransferDetails;
  primaryColor?: string;
  secondaryColor?: string;
  proposalLabel?: string;
  buttonText?: string;
  sectionOrder?: string[];
  heroOverlayOpacity?: number;
  isSetupComplete?: boolean;
  customCoverImages?: string[];
  customGalleryImages?: string[];
  socialLinks?: { facebook?: string; instagram?: string; tiktok?: string; youtube?: string; twitter?: string; linkedin?: string };
  reviewLinks?: {
    google?: string;
    yelp?: string;
    featuredGoogleReview?: { text: string; author: string; rating: number };
    featuredYelpReview?: { text: string; author: string; rating: number };
  };
  /** PDF contracts uploaded by the tenant. Selectable per-estimate from
   *  the Analysis tab. Persisted via the settings.contracts JSONB column. */
  contracts?: ContractFile[];
  /** Append-only history of pricing-relevant settings changes. Lets users
   *  trace "when did break-even jump?" — keep last N records. */
  revisions?: SettingsRevision[];
  /** Schema version of the settings JSON. Bumped on shape migrations. */
  schemaVersion?: number;
}

export interface SettingsRevision {
  ts: string;                       // ISO timestamp
  userEmail?: string;               // who saved
  /** Snapshot of derived metrics at save time, for forensic comparison. */
  derivedMetrics: {
    breakEvenRate: number;
    overheadPerManHour: number;
    targetHourlyRate: number;
    totalOverhead: number;
    totalBillableHours: number;
  };
}

// --- Pricing metrics breakdown (computed by services/metrics.ts) ---

export type MetricsWarningSeverity = 'error' | 'warning';

export type MetricsWarningCode =
  | 'NO_BILLABLE_EMPLOYEES'
  | 'ZERO_OVERHEAD'
  | 'BURDEN_TOO_LOW'
  | 'MARGIN_TOO_HIGH'
  | 'UTIL_OVER_100'
  | 'RATE_BELOW_WAGE'
  | 'NO_EMPLOYEES'
  | 'EQUIPMENT_NO_LIFE'
  // Techos de sensatez y detección de doble conteo — ver services/metrics.ts.
  | 'WAGE_IMPLAUSIBLE'
  | 'OVERHEAD_LINE_IMPLAUSIBLE'
  | 'RATE_IMPLAUSIBLE'
  | 'DUPLICATE_FUEL'
  | 'DUPLICATE_INSURANCE_WC'
  | 'INVALID_INPUT';

export interface MetricsWarning {
  code: MetricsWarningCode;
  /* Clave + valores, no el texto armado — ver services/metrics.ts. */
  severity: MetricsWarningSeverity;
  clave: Clave;
  vars?: Record<string, string | number>;
  anchorId?: string;
}

export interface EmployeeBreakdown {
  id: string;
  name: string;
  role: string;
  /** People represented by this row (>= 1). Lets the breakdown panel print
   *  "Laborer × 3" and makes annualCost/billableHours legible as row totals. */
  headcount: number;
  isBillable: boolean;
  /** Row total — already multiplied by headcount. */
  annualCost: number;
  /** Row total — already multiplied by headcount. */
  billableHours: number;
  effectiveUtilizationPercent: number;
}

export interface OverheadBreakdown {
  id: string;
  name: string;
  annualAmount: number;
}

export interface MetricsBreakdown {
  employees: EmployeeBreakdown[];
  overheadItems: OverheadBreakdown[];
  totalDirectLaborCost: number;
  totalIndirectLaborCost: number;
  totalOverheadFromExpenses: number;
  totalOverheadFromEquipment: number;
  totalOverhead: number;
  totalBillableHours: number;
  warnings: MetricsWarning[];
}

export interface BusinessMetrics {
  overheadPerManHour: number;
  avgLaborBurdenPercent: number;
  avgHourlyWage: number;
  targetHourlyRate: number;
  productionDays: number;
  seasonHours: number;
  totalBillableHours: number;
  totalAnnualCost: number;
  totalOverhead: number;
  breakEvenRate: number;
  /** Detail behind the headline numbers, plus validation warnings.
   *  Populated by services/metrics.calculateBusinessMetrics. */
  breakdown?: MetricsBreakdown;
}

export interface AppNotification {
  id: string;
  type: 'alert' | 'warning' | 'success' | 'info';
  title: string;
  message: string;
  timestamp: string;
  linkTo: ViewState;
}

export enum LeadStatus {
  NEW = 'New',
  CONTACTED = 'Contacted',
  SITE_VISIT = 'Site Visit',
  ESTIMATE_NEEDED = 'Estimate Needed',
  PROPOSAL_SENT = 'Proposal Sent',
  WON = 'Won',
  LOST = 'Lost'
}

export interface Lead {
  id: string;
  name: string;
  phone: string;
  email?: string;
  address: string;
  serviceInterest: string;
  status: LeadStatus;
  notes: string;
  value?: number;
  dateCreated: string;
  nextAction?: string;
  nextActionDate?: string;
  tags?: string[];
  designProjectId?: string; // NEW: Link to a visual presentation
  /** When the lead reached the Won stage. Doble uso: es la marca de cuándo se
   *  cerró el trato, y el candado con el que `claimLeadConversion` se asegura de
   *  que dos pestañas no creen dos clientes. Null en los ganados antes de que
   *  existiera. Ya NO gobierna ningún borrado: un ganado se queda para siempre. */
  wonAt?: string | null;
}

export type ClientTag = string;

export interface Client {
  id: string;
  name: string;
  phone: string;
  email: string;
  address: string;
  totalSpent: number;
  propertyNotes: string;
  gateCode: string;
  preferredContact: 'Phone' | 'Email' | 'Text';
  tags: ClientTag[];
  dateCreated: string;
}

export enum EstimateStatus {
  DRAFT = 'Draft',
  REVIEW_NEEDED = 'Review Needed',
  SENT = 'Sent',
  ACCEPTED = 'Accepted',
  DECLINED = 'Declined',
  EXPIRED = 'Expired',
  // Terminal states (L-1). Accepted remains the canonical "won" state — there is
  // intentionally NO WON member here; the DB CHECK permits 'Won' only as an
  // unused forward-compat synonym. COMPLETED → PAID → ARCHIVED carry an accepted
  // job through to "done & paid & archived out of the active pipeline".
  COMPLETED = 'Completed',
  PAID = 'Paid',
  ARCHIVED = 'Archived'
}

// --- NEW ESTIMATING LOGIC TYPES ---
export interface SiteConditions {
  accessWidthInches: number;      // < 36 triggers hand labor
  soilType: 'Sand' | 'Clay' | 'Rocky';
  slopePercent: number;           // > 2 triggers regarding
  difficultyFactor: number;       // 1.1 - 1.5
  distanceToStreetFt: number;     // > 100 triggers pump
  /** Ambient temperature category. Hot speeds concrete set / strains crew;
   *  Cold slows cure and may require heaters/blankets. Defaults to Normal. */
  weather?: 'Cold' | 'Normal' | 'Hot';
}

export interface EstimateZone {
  id: string;
  name: string;
  notes?: string;
  /** ID of the global service template that seeded this zone. Stamped when a
   *  contractor clicks "Add to Estimate" on a global template card and we
   *  auto-create a zone for the dropped lines. Used at Save time to detect
   *  template-derived zones so we can prompt "save as private template?". */
  sourceTemplateId?: string;
}

/** A workspace-private template, derived from a global template after a
 *  contractor edited prices and chose to save it under their own name. */
export interface PrivateTemplate {
  id: string;
  workspaceId: string;
  name: string;
  /** The global template this was derived from, if any. */
  sourceTemplateId?: string | null;
  /** Frozen snapshot of line items at save time. Shape mirrors
   *  ServiceTemplateItem (description, type, quantity, cost, rate, etc.). */
  items: any[];
  createdBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export type EstimateItemType = 'Labor' | 'Material' | 'Equipment' | 'Subcontractor' | 'Fee';

export interface EstimateItem {
  id: string;
  type: EstimateItemType;
  description: string;
  quantity: number;
  cost: number;
  markupPercent: number;
  marginPercent?: number; // NEW: Standardize on margin for calculations
  rate: number;
  total: number;
  calcBasis?: string;
  /* ── Modelo de precio v2 (PLAN-pricing-model-v2, Parte A) ────────────────
   *
   *  La invariante de una línea: `rate = baseRate × k`, donde k es el factor
   *  de margen neto del estimado y `baseRate` es el precio que produjo la
   *  REGLA de la fila (markup para Material, margen para Labor/Equipment/
   *  Subcontractor, costo pelado para Fee). Lo que se GUARDA es `baseRate`;
   *  `rate` es derivado. Hoy no existe ese eslabón: cada tirón del deslizador
   *  reescribía `rate` encima de sí mismo, así que el precio dependía del
   *  CAMINO —clic al 10% daba 13,46% y arrastrar al 10% daba 5,71%— y volver
   *  el dial a su sitio no devolvía los precios a su sitio.
   *
   *  NO HACE FALTA MIGRACIÓN DE ESQUEMA. Comprobado el 2026-08-09:
   *    - `estimates.items` es `jsonb` (information_schema, proyecto en
   *      producción), así que acepta llaves nuevas sin tocar el esquema.
   *    - `toSnake` (services/database.ts:50-59) es SUPERFICIAL: corta en
   *      `Array.isArray(obj)` y devuelve el arreglo tal cual, sin recorrer
   *      sus elementos. Los nombres de estas llaves viajan en camelCase y
   *      vuelven igual — que es justo lo que hace falta, porque `toCamel`
   *      tampoco baja al arreglo.
   *    - `estimateAllowedKeys` (services/database.ts:245-270) permite
   *      `'items'` entero, no llave por llave.
   *  Las filas viejas llegan sin estos campos → `undefined` → falsy → el
   *  comportamiento de hoy, intacto. Por eso todos son opcionales. */
  /** El precio que produjo la REGLA de la fila, ANTES del factor global k.
   *  Es el valor persistido y el único desde el que se recalcula: `rate` sale
   *  de multiplicarlo por k (salvo pase directo, que nunca escala). Ausente en
   *  toda fila anterior a este modelo; quien lo necesite lo re-deriva del
   *  costo y la política del estimado. */
  baseRate?: number;
  /** El contratista fijó esta línea a mano.
   *  Un pin marca la LÍNEA BASE de la fila, NO la congela: el dial de margen
   *  neto la sigue escalando junto con todas las demás (Parte A.3). Congelarla
   *  volvería insoluble el solver en cuanto hubiera suficientes filas fijadas,
   *  y obligaría a pantallas de "quita el pin a 4 líneas para continuar". */
  pricePinned?: boolean;
  /** El porcentaje que TECLEÓ el contratista, tal cual lo tecleó.
   *  Se guarda literal para no volver a enseñar un número que nadie escribió
   *  (Parte A.5): si tecleó 30, la tarjeta dice 30, no el 42,9% compuesto que
   *  originó esta auditoría. */
  pinnedPercent?: number;
  /** Sobre qué base se interpreta `pinnedPercent`. NUNCA se deduce del tipo al
   *  leer: un mismo 30 vale $26,00 como markup y $28,57 como margen sobre un
   *  costo de $20, así que el metro viaja con el número.
   *    'markup' → sobre el COSTO   ·  'margin' → sobre el PRECIO
   *    'rate'   → no hay porcentaje; el contratista tecleó la tarifa entera.
   *  Cambiar el tipo de la fila BORRA el pin (Parte A.3): el metro dejaría de
   *  significar lo mismo y arrastrar el número cruzaría dos escalas distintas. */
  pinnedMetric?: 'markup' | 'margin' | 'rate';
  isOverridden?: boolean;
  category?: string;       // Optional category for grouping (e.g. "Concrete", "Hardscape")
  /** Schema v3: catalog_items.id this row was materialized from. When set,
   *  cost edits auto-save to the workspace's pricebook keyed by this id. */
  sourcePricebookId?: string;
  unit?: string;           // NEW: Unit of measure (e.g., SF, CY, EA)
  /** Lo que ve el CLIENTE, cuando no es lo mismo que se cobra.
   *
   *  La mano de obra se cotiza en horas-hombre y eso vive en `quantity`. Pero
   *  un documento que dice "32 HR" al lado de un total visible despeja la
   *  tarifa por hora con una división, y encima la descripción de esa misma
   *  fila sigue hablando de "2 crew-days". El motor de costo lee `quantity`;
   *  la propuesta lee esto.
   *
   *  Ausente = fila anterior al Esquema v4, o tecleada a mano, y entonces
   *  `quantity`/`unit` YA son la medida del cliente. */
  clientQty?: number;
  clientUnit?: string;
  /** Cómo salió la fila del soltado de plantilla, para el diff de guardado.
   *
   *  Ese diff pregunta "¿esto lo editó el contratista?", y el archivo de la
   *  plantilla no puede contestarlo: declara `unitPriceUsd: 0` en las 6.604
   *  partidas del corpus, mientras que al soltar se aplica el precio del
   *  taller. Comparar contra el archivo marcaba como "cambiada" toda fila de
   *  un taller que sí tiene precios cargados. */
  dropQty?: number;
  dropCost?: number;
  zoneId?: string;       // NEW: Link to Zone
  assemblyId?: string;   // NEW: Link to source Assembly
}

export interface PricingSnapshot {
  overheadPerManHour: number;
  laborBurdenPercent: number;
  targetMarginPercent: number;
  baseLaborCost: number;
  targetHourlyRate: number;
  materialMarkupPercent: number;
  breakEvenRate?: number;
  /** ISO timestamp the snapshot was taken. Lets the estimate UI flag drift
   *  against current settings. */
  capturedAt?: string;
  /** Bumped when the pricing-relevant settings shape changes. Lets older
   *  snapshots be detected and offered for refresh. */
  settingsSchemaVersion?: number;
  /** Human-readable trace for the Analysis tab. */
  breakdownSummary?: {
    directLaborCost: number;
    totalOverhead: number;
    billableHours: number;
  };
}

export interface EstimateAdjustments {
  tripCharge: boolean;
  emergencySurcharge: boolean;
  applyTax: boolean;
  taxLabor: boolean;
  taxRate: number;
  minJobFeeApplied: boolean;
  discount: number;
  discountType: 'flat' | 'percent';
  /** Optional deposit percentage shown in Analysis. The deposit dollar amount
   *  is computed live as grandTotal × depositPercent / 100. */
  depositPercent?: number;
}

/** Type of a single placeable field on a contract PDF. */
export type SignatureFieldType = 'signature' | 'initial' | 'date' | 'text';

/** Whose action this field captures. The proposal-signing flow uses this to
 *  decide which fields the client must complete vs which are pre-filled by
 *  the contractor. */
export type SignatureFieldRole = 'client' | 'contractor';

/** A single placeable field on a contract PDF page. Coordinates are stored
 *  as fractions of the page dimensions (0..1) so they remain stable across
 *  zoom levels and device pixel ratios. */
export interface SignatureField {
  id: string;
  type: SignatureFieldType;
  role: SignatureFieldRole;
  page: number;            // 1-indexed
  xPercent: number;        // 0..1, top-left X
  yPercent: number;        // 0..1, top-left Y
  widthPercent: number;    // 0..1
  heightPercent: number;   // 0..1
  label?: string;          // Optional placeholder for 'text' fields
  required?: boolean;      // Defaults to true at usage time
}

/** A PDF contract uploaded by the tenant in Settings → Contracts. The file
 *  itself lives in Supabase Storage; this metadata lives on
 *  BusinessSettings.contracts so it travels with the workspace. */
export interface ContractFile {
  id: string;
  name: string;
  url: string;
  path: string;       // Storage path used to delete
  uploadedAt: string;
  /**
   * El idioma en el que está redactado el documento.
   *
   * No se traduce nunca —es un PDF subido, con campos de firma colocados por
   * coordenadas— y no hace falta: quien lo sube ya decidió en qué idioma se lo
   * manda a su cliente. Se guarda para poder AVISAR del desajuste: nada impedía
   * mandar una propuesta en inglés con un contrato adjunto en español, y el
   * cliente acababa firmando algo que no entiende.
   *
   * Opcional: los contratos ya subidos no lo traen y entonces no se avisa nada,
   * que es lo que se hacía hasta ahora.
   */
  idioma?: Idioma;
  sizeBytes?: number;
  /** Cached page count, populated the first time the editor is opened. */
  pageCount?: number;
  /** Field map placed by the tenant in Settings → Contracts → Configure.
   *  Empty/undefined means the contract isn't ready to be sent for signature. */
  signatureFields?: SignatureField[];
  /** The uploaded PDF already carries the contractor's signature on paper
   *  (user answered "already signed" in the post-upload prompt). The field
   *  editor then only offers client-role fields and no contractor
   *  counter-signature is collected for this contract. */
  preSigned?: boolean;
  /** Copia LIMPIA del mismo contrato, sin ninguna firma encima.
   *
   *  Solo tiene sentido en los `preSigned`: ahí la firma del contratista son
   *  píxeles del archivo, así que el ejemplar que se adjunta al PDF para firmar
   *  a mano saldría con esa firma escaneada dentro. Con esta copia, el que se
   *  imprime va en blanco y las dos partes firman de su puño.
   *
   *  Opcional a propósito: quien no la suba sigue funcionando como siempre —se
   *  imprime el archivo principal—, y los contratos anteriores a esto no la
   *  tienen. NO sustituye al archivo principal en ningún otro sitio: la firma en
   *  línea, el archivado legal y el editor de campos siguen sobre `url`/`path`,
   *  que es el documento del que se autorizaron las posiciones. */
  blankUrl?: string;
  blankPath?: string;
  /** Contractor counter-signature, captured once when configuring the contract
   *  ("Sign as contractor"). Keyed by SignatureField.id (the contractor-role
   *  fields). Baked into the template so every proposal using this contract shows
   *  the contractor's signature already in place before the client signs.
   *
   *  Los campos de tipo `date` que haya aquí NO se leen tal cual: se reescriben
   *  con la fecha de envío de cada propuesta (`services/contractDates`), porque
   *  la plantilla se firma una vez y se reutiliza durante meses. */
  contractorSignedFields?: Record<string, string>;
  /** Typed legal name of the contractor who counter-signed. */
  contractorSignedBy?: string;
  /** ISO timestamp the contractor counter-signed. */
  contractorSignedAt?: string;
  /** Hojas del contrato ya rasterizadas y guardadas, con la contrafirma dentro,
   *  listas para adjuntarse al PDF de la propuesta sin volver a procesar nada.
   *  Se construyen al subir el contrato y al contrafirmarlo — no en cada
   *  descarga, que es lo que tardaba diez segundos. Ausente en los contratos
   *  subidos antes de esto: entonces se rasteriza al vuelo, como siempre. */
  printPages?: StoredContractPage[];
  /** Qué versión del contrato produjo esas hojas. Si no coincide con el
   *  contrato actual, están caducadas y se rehacen — ver contractPrintStamp. */
  printPagesStamp?: string;
}

/** Una hoja del contrato ya rasterizada y subida al bucket. */
export interface StoredContractPage {
  /** Ruta en el bucket, para poder borrarla con el contrato. */
  path: string;
  /** URL pública permanente. Va dentro de propuestas que ve gente anónima, así
   *  que no puede ser una URL firmada que caduque en una hora. */
  url: string;
  /** Tamaño natural en px, para conservar la proporción al imprimir. */
  width: number;
  height: number;
}

export interface ProposalUpsell {
  id: string;
  name: string;
  price: number;
  description: string;
}

/** P-5: a structured portfolio image on a proposal (replaces the flat string[]). */
export interface GalleryItem {
  url: string;
  /** Ruta del objeto en el bucket, para identificarlo (depurar, medir, migrar).
   *  NO es para borrarlo: las imágenes de propuesta no se borran nunca — ver la
   *  nota de retención en services/storage.ts. */
  storagePath?: string;
  /** Optimized thumbnail URL (optional). */
  thumbUrl?: string;
  caption?: string;
  /** Position in the gallery; lower renders first. */
  order: number;
  /** Marks the feature/hero tile (spans the bento grid). */
  isHero?: boolean;
  /** Before/after pairing — this item is the "before", paired with an "after". */
  beforeAfterPair?: { afterUrl: string; afterStoragePath?: string };
}

/** Cómo se dibuja el desglose de precios en la propuesta del cliente. */
export type PricingLayoutMode = 'itemized' | 'grouped';

/** Eje por el que se agrupan las partidas en modo agrupado.
 *  'single' = todo en una sola sección («Proyecto completo»). */
export type PricingGroupBy = 'type' | 'zone' | 'category' | 'single';

/**
 * Cómo se enseña una sección al cliente.
 *  'expandable' — encabezado + total; las partidas se abren al tocar.
 *  'open'       — las partidas siempre a la vista.
 *  'summary'    — solo encabezado y total, sin forma de abrirlo.
 */
export type PricingSectionDisplay = 'expandable' | 'open' | 'summary';

/**
 * Retoques de presentación de UNA sección.
 *
 * `key` es la clave DERIVADA del agrupamiento ('type:Labor', 'zone:<id>',
 * 'category:Concreto', 'all'), nunca una lista de partidas. Esa es la
 * diferencia que importa: al añadir una partida nueva al estimado, cae sola en
 * su sección. Si aquí se guardaran ids, la partida nueva no pertenecería a
 * ninguna sección — invisible para el cliente y aun así dentro del total.
 *
 * No hay `hidden`, y es a propósito: se puede dejar de desglosar una sección,
 * pero no esconderla. Un bloque de trabajo que el cliente está pagando y no ve
 * es la única forma de que este rediseño mienta, así que no existe ese
 * interruptor. Por la misma razón el TOTAL de la sección se enseña siempre: es
 * lo que hace que las secciones sumen el total y la propuesta se pueda cuadrar.
 */
export interface PricingSectionOverride {
  key: string;
  /** Nombre a la vista. Vacío = el que le toca por defecto. */
  name?: string;
  /** Una línea opcional: qué incluye. */
  description?: string;
  display?: PricingSectionDisplay;
  /**
   * Desglosar el precio PARTIDA POR PARTIDA dentro de la sección.
   *
   * Apagado, el cliente sigue viendo lo que cuesta la sección —y puede abrirla y
   * leer todo lo que incluye— pero sin un precio al lado de cada renglón. Es
   * «no te desgloso la mano de obra», no «no te digo cuánto cuesta».
   *
   * Nunca afecta al total de la sección. Ese no se puede apagar.
   */
  showItemPrices?: boolean;
  /** Posición. Ausente = el orden natural del agrupamiento. */
  order?: number;
}

/**
 * Cómo se lee UNA partida en la propuesta del cliente.
 *
 * El estimado se escribe para trabajar: «Paver crew», «1 crew-day». Eso es lo
 * correcto para costear y lo que menos le dice al cliente, que lee su patio y
 * no una cuadrilla. Esto deja reescribir esa línea SOLO para el documento.
 *
 * Vive en la propuesta, no en la partida, y esa es la diferencia que importa:
 * el estimado conserva sus palabras y sus cantidades intactas —el costeo sigue
 * leyendo `quantity`— y dos propuestas del mismo estimado pueden contarlo de
 * dos maneras. Renombrar aquí no mueve un céntimo.
 *
 * No hay forma de ocultar una partida, por lo mismo que no la hay de ocultar
 * una sección: el cliente paga por todas.
 */
export interface PricingItemOverride {
  /** EstimateItem.id */
  id: string;
  /** Lo que se lee en grande. Vacío = la descripción del estimado. */
  description?: string;
  /** La línea de debajo («Qty 1 crew-day»). Vacío = la que sale de la
   *  cantidad y la unidad del cliente. Texto libre: cabe «incluye 2 oficiales»
   *  igual que «1 jornada». */
  detail?: string;
}

/**
 * El contenido de la propuesta en un idioma que NO es en el que se escribió.
 *
 * Vive aparte del contenido original y nunca lo pisa. Esa es la regla que hace
 * que traducir sea reversible: si la IA estropea la descripción de una partida,
 * lo que escribiste sigue intacto un clic más allá.
 *
 * Solo lleva los campos que son texto para leer. Los precios, las cantidades y
 * las unidades no se traducen — son datos, y traducir un número es como se
 * rompen los presupuestos.
 */
export interface TraduccionPropuesta {
  title?: string;
  introMessage?: string;
  scopeOfWork?: string;
  customTerms?: string;
  warrantyText?: string;
  companyProfileText?: string;
  projectConditionsLabel?: string;
  /** La descripción de cada partida, por `EstimateItem.id`. */
  partidas?: Record<string, string>;
  /** Los extras opcionales, por `ProposalUpsell.id`. */
  upsells?: Record<string, { name?: string; description?: string }>;
  /**
   * Las reescrituras por partida (`pricingItems`), por `EstimateItem.id`.
   *
   * Estas NO son un extra: son obligatorias. `resolverItems` hace
   * `override?.description || item.description`, así que una reescritura sin
   * traducir TAPA la traducción de la partida. Sin esto, traducir una propuesta
   * con partidas renombradas no se nota en pantalla.
   */
  reescrituras?: Record<string, { description?: string; detail?: string }>;
  /** Los nombres de sección curados (`pricingSections`), por `key`. */
  secciones?: Record<string, { name?: string; description?: string }>;
  /** Los pies de foto de la galería, por `url` (GalleryItem no tiene id). */
  galeria?: Record<string, string>;
  /**
   * Huella del contenido original cuando se tradujo.
   *
   * Sin esto, editar el alcance después de traducir deja al cliente leyendo la
   * versión vieja sin que nadie se entere — el fallo silencioso más caro de
   * todo esto, porque el documento parece correcto. Cuando la huella de ahora
   * no cuadra con esta, la pestaña Enviar dice que la traducción se quedó
   * atrás. Mismo patrón que `scopeGeneratedFromItemsHash`.
   */
  huella?: string;
  /** ISO. Solo informativo, para poder decir «traducida el …». */
  generadaEn?: string;
  /**
   * Los términos y la garantía los revisó una persona.
   *
   * Se guarda aparte del resto porque es lo único de aquí que es un
   * compromiso legal: una cláusula de garantía mal traducida cambia lo que
   * prometes. Mientras esto sea falso, la pestaña Enviar lo pide.
   */
  legalRevisado?: boolean;
}

export interface ProposalSettings {
  /**
   * El idioma en el que está ESCRITO el contenido de la propuesta.
   *
   * No confundir con `idioma`, que es en el que se le entrega al cliente.
   * Cuando los dos coinciden no hay nada que traducir; cuando no, se usa
   * `traducciones[idioma]`.
   */
  idiomaOriginal?: Idioma;
  /** El contenido traducido, por idioma de destino. El original no se toca. */
  traducciones?: Partial<Record<Idioma, TraduccionPropuesta>>;
  /**
   * El idioma en el que se le enseña la propuesta al CLIENTE.
   *
   * Hasta ahora no se guardaba, y el visor público resolvía el idioma con
   * `detectarIdioma()` — o sea, con el navegador y el localStorage de quien
   * abre el enlace. Dos consecuencias, las dos malas:
   *
   *   · El contratista que trabajaba en inglés mandaba una propuesta que le
   *     llegaba al cliente en español.
   *   · Como `detectarIdioma()` cae a español salvo que el navegador diga
   *     «en», el idioma de la propuesta lo decidía, en la práctica, el azar
   *     de la configuración del cliente.
   *
   * Se sella al crear la propuesta y se refresca cada vez que el editor
   * guarda: la propuesta sale en el idioma en el que la estabas trabajando.
   * Una vez mandada nadie la vuelve a tocar, así que queda congelada.
   *
   * Opcional a propósito: las propuestas de antes de este campo no lo tienen,
   * y quien las lee cae a español, que es exactamente lo que hacían ayer.
   */
  idioma?: Idioma;
  title: string;
  introMessage: string;
  /** S-1: the formal Scope of Work, kept separate from introMessage (a short
   *  greeting) so regenerating the SOW no longer destroys a hand-written note.
   *  Rendered as markdown (S-2). Falls back to introMessage when unset for
   *  estimates created before the split. */
  scopeOfWork?: string;
  /** S-4: hash of the estimate items at the time the AI scope was last generated.
   *  When the current items hash differs, the editor shows a "scope may be out of
   *  date — regenerate" hint. */
  scopeGeneratedFromItemsHash?: string;
  coverImageTheme: 'Modern' | 'Natural' | 'Elegant' | 'Custom';
  customCoverImageUrl?: string;
  /** How the cover fills the hero. 'cover' (default) crops to fill the band;
   *  'contain' fits the whole image inside it — what an uploaded flyer, poster
   *  or logo needs, since cropping those cuts off the part that matters. */
  coverImageFit?: 'cover' | 'contain';
  /** Which part of the cover stays in frame, as CSS object-position
   *  percentages. Defaults to dead center (50/50). */
  coverImagePosition?: { x: number; y: number };
  showLineItemPricing: boolean;
  showTotals: boolean;
  /** Cómo se le PRESENTA el desglose al cliente. No toca el precio: las mismas
   *  partidas, los mismos totales, dibujados de otra forma.
   *  'itemized' = una fila por partida (lo de siempre).
   *  'grouped'  = secciones curadas ("Mano de obra", "Materiales"…). */
  pricingLayout?: PricingLayoutMode;
  /** Eje por el que se agrupan las partidas en modo agrupado. */
  pricingGroupBy?: PricingGroupBy;
  /** Retoques de presentación por sección. Las secciones se DERIVAN de las
   *  partidas; esto solo las viste. Ver utils/proposalSections.ts. */
  pricingSections?: PricingSectionOverride[];
  /** Reescrituras por partida, solo para el documento. Se aplican en los dos
   *  modos: si el cliente lee «Patio trasero» en agrupado, lo lee igual en
   *  detallado y en el PDF. Ver PricingItemOverride. */
  pricingItems?: PricingItemOverride[];
  /** Cómo se llama en la propuesta el recargo por condiciones del terreno
   *  (`siteConditions.difficultyFactor`). Por defecto «Project Conditions»:
   *  «recargo por dificultad» es verdad, pero no se le dice a un cliente. */
  projectConditionsLabel?: string;
  upsells?: ProposalUpsell[];
  animationStyle?: 'None' | 'Fade' | 'Slide' | 'Zoom';
  showGallery?: boolean;
  /** @deprecated P-5 — legacy flat list. Kept for back-compat read; the editor now
   *  writes the structured `galleryItems`. The renderer falls back to this when
   *  galleryItems is absent. */
  galleryImages?: string[];
  /** P-5: structured portfolio items (caption, order, hero, before/after). */
  galleryItems?: GalleryItem[];
  /** P-5: url of the chosen hero/feature tile (overrides isHero / first-item default). */
  heroImageUrl?: string;
  showCompanyProfile?: boolean;
  companyProfileText?: string;
  accentColor?: string;
  fontFamily?: 'Sans' | 'Serif' | 'Mono';
  customTerms?: string;
  paymentLink?: string;
  /** Offer the card/Stripe payment link on this proposal. Defaults to on when a
   *  paymentLink is present (back-compat); set false to turn it off per proposal. */
  showStripe?: boolean;
  showZelle?: boolean;
  zelleEmail?: string;
  zellePhone?: string;
  /** Cobrar esta propuesta por transferencia bancaria. Al prenderlo se copian
   *  aquí los datos del negocio (igual que Zelle) porque la vista pública no
   *  tiene contexto de workspace — y así la cuenta solo viaja en las propuestas
   *  donde se eligió este método. */
  showBankTransfer?: boolean;
  bankTransfer?: BankTransferDetails;
  /** Offline / cash job: no online payment method is offered. Lets the proposal be
   *  shared without Stripe or Zelle; after signing, the client is told the business
   *  will reach out about payment. */
  cashPayment?: boolean;
  // New Customization Fields
  primaryColor?: string; // Main brand color (headers, buttons)
  secondaryColor?: string; // Accent elements
  proposalLabel?: string; // e.g. "Estimate", "Quote", "Proposal"
  buttonText?: string; // e.g. "Sign & Accept", "Approve"
  sectionOrder?: string[]; // IDs of sections in order
  visibleSections?: string[];
  heroOverlayOpacity?: number; // 0.0 to 1.0
  showGoogleReviews?: boolean;
  showYelpReviews?: boolean;
  suggestUpsells?: boolean;
  showWarranty?: boolean;
  selectedWarrantyId?: string;
  warrantyText?: string;
  showSocialLinks?: boolean;
  upsellStrategy?: string;
  /** Contract attached from the tenant's settings library. Resolved to a
   *  ContractFile at render time via BusinessSettings.contracts. */
  attachedContractId?: string;
}

export interface Estimate {
  id: string;
  clientId: string;
  clientName: string;
  workspaceId?: string; // Appended by getEstimateDoc for anon routing
  items: EstimateItem[];
  total: number;
  status: EstimateStatus;
  dateCreated: string;
  lastUpdated: string;
  validUntil: string;
  pricingSnapshot: PricingSnapshot;
  /** La meta de margen neto de ESTE estimado, en porcentaje sobre el PRECIO y
   *  medida SOBRE EL TRABAJO PROPIO — los Fee de pase directo quedan fuera del
   *  numerador y del denominador (Parte A.4).
   *
   *  Por qué sobre el trabajo propio: los dólares de ganancia son idénticos de
   *  las dos maneras, porque un pase directo no aporta ninguno. Lo único que
   *  cambia es el denominador. Excluirlo hace el porcentaje comparable entre
   *  trabajos sin importar cuántos permisos hubo de por medio, e impide que el
   *  permiso de un cliente te obligue a subir TUS precios: con un permiso de
   *  $400 y con uno de $40.000 el factor k es el mismo (1,087).
   *
   *  Vive en el estimado, y no en los ajustes del taller, porque los diales
   *  globales son solo la SEMILLA (decisión 2): al crear el estimado se copian
   *  y desde ahí cada uno va por su cuenta. Ausente = nunca se movió el dial;
   *  eso equivale a k = 1, "exactamente mis diales", que es el ancla honesta.
   *
   *  OJO, PERSISTENCIA PENDIENTE: hoy este campo NO llega a la base. No hay
   *  columna `net_margin_target` en `estimates` (comprobado contra producción
   *  el 2026-08-09) ni entrada en `estimateAllowedKeys`, así que `filterEstimate`
   *  lo descarta al guardar. Quien cablee el dial (olas 3-B/4) tiene que
   *  resolverlo antes: o columna + llave en la lista blanca, o meterlo dentro
   *  de `pricingSnapshot`, que ya es jsonb y ya está permitido. Declararlo
   *  ahora es aditivo y no cambia ningún valor; olvidarlo después perdería la
   *  meta en silencio al recargar. */
  netMarginTarget?: number;
  adjustments?: EstimateAdjustments;
  proposalSettings?: ProposalSettings;
  signature?: string; // Base64 signature
  signedDate?: string;
  /** ID of the ContractFile that was signed alongside this proposal. Frozen
   *  at sign-time so future edits to the contracts library don't retroactively
   *  change what the client agreed to. */
  signedContractId?: string;
  /** Per-field captured values from the contract signing flow. Keyed by
   *  SignatureField.id. Signature/initial values are base64 PNGs; date and
   *  text fields are plain strings. */
  contractSignedFields?: Record<string, string>;
  /* ── Archived executed contract (server-written, read-only here) ──────────
   * Produced by the archive-signed-contract edge function at signing time and
   * stored in the private `signed-contracts` bucket. These are intentionally
   * ABSENT from estimateAllowedKeys in services/database.ts, so the client can
   * never write them — the archive is evidence, and only the server mints it. */
  /** Path inside the private bucket. Undefined = signed but not yet archived. */
  executedContractPath?: string;
  /** SHA-256 of the stamped document BEFORE the audit page was appended, so the
   *  hash printed on that page can be independently recomputed. */
  executedContractSha256?: string;
  executedContractAt?: string;
  siteConditions?: SiteConditions; // NEW
  zones?: EstimateZone[];          // NEW
  selectedWarrantyId?: string;     // NEW
  warrantyText?: string;           // NEW
  /** Cuándo abrió el cliente la propuesta. Lo sella el enlace público, y el
   *  tablero de Seguimientos lo lee para separar «Enviadas» de «Abiertas».
   *  `null` —y no `undefined`— para borrarlo: el filtro de escritura descarta
   *  las claves undefined, así que devolver una propuesta a «Enviadas» a mano
   *  tiene que mandar null explícito. Mismo motivo que en `sentAt`. */
  viewedAt?: string | null;
  followUp1SentAt?: string;        // Tracks when the unread follow-up was sent
  followUp2SentAt?: string;        // Tracks when the post-view follow-up was sent
  followUpsPaused?: boolean;       // Tracks whether automated follow-ups are manually disabled
  createdBy?: string;              // NEW: ID of the user who created the estimate, for routing follow-ups
  createdByName?: string;          // NEW: Name of the creator for UI display
  /** Lifetime count of automated follow-ups across all phases. Display only —
   *  the per-phase maxEmails caps read the two counters below. */
  automatedFollowUpsSent?: number;
  /** Reminders sent while the proposal was still unopened (F6). */
  followUpsSentUnread?: number;
  /** Check-ins sent after the client opened it (F6). Split from the single
   *  counter, which was never reset on the unread→viewed transition and so let
   *  the unread phase exhaust the viewed phase's budget. */
  followUpsSentViewed?: number;
  lastAutomatedFollowUpAt?: string; // Timestamp of the last email sent
  convertedToProjectId?: string;    // Tracks the project ID created from this estimate
  tradeId?: string;
  /** Job scope picked in the Start a Job flow (category id from trade templates). */
  scopeCategoryId?: string;
  scopeLabel?: string;
  /** Reason the client gave when declining the proposal, captured in the public
   *  decline flow (F-6). Persisted via respond_to_proposal's p_project_data jsonb
   *  and surfaced in Estimates/Follow-ups. */
  declineReason?: string;
  /** Terminal-state timestamps (L-1). archivedAt: archived out of the active
   *  pipeline. paidAt: the converted job was fully paid (manual for now — no
   *  invoices table yet, so automatic Paid firing is deferred). */
  archivedAt?: string;
  paidAt?: string;
  /** Real timestamp of when the proposal email was actually sent (F-2), distinct
   *  from lastUpdated. Set by the Share-tab send success handler. */
  /** Cuándo salió de verdad la propuesta. `null` la borra en el servidor —
   *  `undefined` lo descarta el filtro de escritura, así que "Deshacer" tras
   *  marcarla como enviada tiene que mandar null explícito. */
  sentAt?: string | null;
  /** Prospecto para el que se armó esta propuesta, cuando se hizo ANTES de que
   *  el cliente existiera. Mientras esté puesto, `clientId` va vacío: la
   *  propuesta cuelga del prospecto. Al ganarse el trato (firma, Pagada, o
   *  conversión manual) el prospecto pasa a cliente, `clientId`/`clientName` se
   *  rellenan y esto vuelve a `null` — ver utils/leadProposals.ts. */
  sourceLeadId?: string | null;
}

// --- DESIGN & PORTFOLIO TYPES ---
export interface DesignProject {
  id: string;
  title: string;
  description: string;
  beforeImageUrls: string[];
  afterImageUrls: string[];
  design3dUrl?: string; // Optional URL for iframe embeds (e.g. Sketchfab)
}

export enum ProjectStatus {
  ACCEPTED = 'Accepted',
  SCHEDULED = 'Scheduled',
  IN_PROGRESS = 'In Progress',
  COMPLETED = 'Completed',
  CANCELLED = 'Cancelled'
}

export type TaskPhase = 'Mobilization' | 'Execution' | 'Finish';

export interface ProjectTask {
  id: string;
  description: string;
  isCompleted: boolean;
  assigneeId?: string;
  completedBy?: string;   // Name of the person who completed the task
  completedAt?: string;   // ISO timestamp of completion
  priority?: 'Low' | 'Medium' | 'High';
  dueDate?: string;
  phase?: TaskPhase;          // NEW: explicit phase tag (replaces array-slice grouping)
  estimateItemIds?: string[]; // NEW: links a task back to one or more EstimateItem.id values
}

export interface ProjectExpense {
  id: string;
  date: string;
  vendor: string;
  category: 'Material' | 'Equipment' | 'Subcontractor' | 'Other';
  amount: number;
  description: string;
  receiptUrl?: string;   // Public URL of the uploaded receipt photo (Supabase Storage)
  receiptPath?: string;  // Storage path — used when deleting the receipt
}

export interface ProjectInvoice {
  id: string;
  /** When the invoice was issued. */
  date: string;
  dueDate: string;
  amount: number;
  status: 'Draft' | 'Sent' | 'Paid' | 'Overdue';
  /** When it was actually marked paid. Optional — invoices predating the status
   *  picker have none, so revenue reporting falls back to `date`. Cleared if the
   *  invoice is moved back out of Paid. */
  paidAt?: string;
  /** Folio que teclea la persona ("24124"). Existe porque HOY el folio y la
   *  identidad son el mismo campo: ProjectFinancials.tsx:246 mete lo tecleado
   *  en `id`, de modo que dos facturas con el mismo folio se pisan y un folio
   *  mal escrito ya no se puede corregir sin perder la fila. En producción hay
   *  una llamada "24124 " — con espacio final — y ésa es su identidad.
   *  Nadie lo escribe todavía (0 de 2 facturas lo traen): separar folio de
   *  identidad es la migración de la Ola 2, aquí sólo se declara el hueco. */
  number?: string;
  /** Condiciones de pago: 'Net 30', 'Due on Receipt'… El selector de Términos
   *  existe en pantalla desde siempre (ProjectFinancials.tsx:139 y :739-750) y
   *  no hace absolutamente nada: el estado se llena y se tira al guardar
   *  porque el tipo no tenía dónde ponerlo. Es informativo — la fecha que
   *  manda para vencimientos sigue siendo `dueDate`, no esto. Declararlo es lo
   *  único de esta ola; conectar el selector no. */
  terms?: string;
  items?: any[];
}

export interface ProjectNote {
  id: string;
  date: string;
  author: string;
  content: string;
  type: 'Note' | 'Milestone' | 'Issue' | 'Delay';
  /** Optional photo attached to a Site Journal entry (public URL + storage path). */
  photoUrl?: string;
  photoPath?: string;
}

/**
 * DECLARADO, NO CABLEADO — NADIE ESCRIBE ESTO TODAVÍA.
 *
 * La columna `projects.change_orders` (jsonb) existe en producción y las 59
 * filas la traen vacía. No hay pantalla que dé de alta una orden de cambio, no
 * hay RPC que la siembre y ningún cálculo la lee.
 *
 * NO LO USES PARA CALCULAR. Un `changeOrders` vacío no significa "esta obra no
 * tuvo trabajo extra", significa "el trabajo extra no se apunta aquí". Sumar
 * estos importes al valor del contrato daría cero en todos los espacios de
 * trabajo y parecería correcto: ésa es exactamente la trampa que este
 * comentario existe para evitar.
 *
 * El muñón es la raíz de un defecto real. Sin una orden de cambio aprobada, el
 * valor del contrato no puede crecer legítimamente, así que una factura de
 * trabajo extra no tiene dónde encajar y acaba restándose del ingreso en vez de
 * sumarse. Darle campos aquí no lo arregla; cablearlo (alta, aprobación y suma
 * al contrato) es Ola 3.
 *
 * Todo salvo `id` es opcional a propósito: no hay ni una fila guardada que
 * cumpliría un campo obligatorio, y exigirlo rompería a quien escriba la
 * primera.
 */
export interface ChangeOrder {
  id: string;
  /** Qué trabajo extra se acordó, en palabras del contratista. */
  description?: string;
  /** Importe del cambio. Positivo suma al contrato; negativo es un crédito
   *  (alcance retirado). Nadie lo suma todavía — ver el bloque de arriba. */
  amount?: number;
  /** Vocabulario PROPUESTO, no observado: no hay una sola fila guardada de la
   *  que deducirlo. Quien lo cablee en la Ola 3 puede ampliarlo sin migrar
   *  datos, justo porque no hay datos. */
  status?: 'Draft' | 'Pending' | 'Approved' | 'Rejected';
  /** Fecha de emisión (ISO). */
  date?: string;
  /** Cuándo pasó a 'Approved' (ISO). Va aparte de `date` porque lo que hace
   *  crecer el contrato es la aprobación, no el haberlo redactado: una orden
   *  emitida y sin firmar no es dinero. */
  approvedAt?: string;
  /** Quién aprobó: UUID de `allowed_users.id`, la vía canónica de identidad.
   *  Se guarda el id y no el nombre —como hace ProjectTask.completedBy— para
   *  que no se congele el día que esa persona se cambie el nombre. */
  approvedBy?: string;
}

export type MaterialStatusValue = 'Pending' | 'Ordered' | 'Ready' | 'Installed';

export interface ProjectMaterialStatus {
  estimateItemId: string;       // empty or starts with 'adhoc_' for non-estimate items
  status: string;               // typically MaterialStatusValue but stored as text
  poNumber?: string;             // optional purchase order number
  description?: string;          // NEW: human-readable item name (replaces poNumber-as-description hack)
  vendor?: string;               // NEW: vendor (e.g., Lowe's, Home Depot) for grouping the shopping list
  quantity?: number;             // NEW: editable quantity
  cost?: number;                 // NEW: editable per-unit cost
}

export interface Project {
  id: string;
  clientId: string;
  clientName: string;
  estimateId?: string | null;
  name: string;
  status: ProjectStatus;
  startDate: string;
  endDate: string;
  revenue: number;
  description: string;
  /** Canonical worker assignment (identity_v2). UUIDs from allowed_users.id. */
  assignedUserIds?: string[];
  /** @deprecated — use assignedUserIds. Kept for dual-write/read during cleanup. */
  assignedCrewIds: string[];
  completionPercent: number;
  tasks: ProjectTask[];
  materialStatus: ProjectMaterialStatus[];
  changeOrders: ChangeOrder[];
  notes: ProjectNote[];
  estimatedLaborHours?: number;
  /** @deprecated Derived live from time_logs in the UI as of Phase 3. Column kept for backfill but no longer read. */
  actualLaborHours?: number;
  expenses?: ProjectExpense[];
  invoices?: ProjectInvoice[];
  assignedEquipmentIds?: string[];
  siteCoordinates?: { lat: number; lng: number };
  geofenceRadiusFt?: number;
  reviewRequestSentAt?: string;
  reviewRequestPaused?: boolean;
  /** When set, the "auto-flip linked materials when a task is completed" prompt is suppressed and uses this value. */
  autoCompleteMaterials?: boolean;
  /** Lifecycle timestamps stamped server-side by project_transition_status (P4). */
  acceptedAt?: string;
  scheduledAt?: string;
  actualStartDate?: string;
  completedAt?: string;
  cancelledAt?: string;
  archivedAt?: string;
  cancelReason?: string;
  /**
   * @deprecated UNUSED. Nothing reads or writes these.
   *
   * These held a single "current lead" with a handover log. The lead is now
   * PER DAY and derived from the schedule instead — whoever is booked on the job
   * that date is in charge that date (see utils/projectDailyLead.ts). A stored
   * copy would be a second answer to the same question, free to drift from the
   * board.
   *
   * The columns still exist in Postgres (migration 20260810120000). They are
   * left in place rather than dropped so no deploy can land ahead of a rollback,
   * and they carry no data worth keeping.
   */
  leadUserId?: string | null;
  /** @deprecated UNUSED — see leadUserId. */
  leadHistory?: unknown[];
}

export type PricebookCategory = string;

export interface PricebookItem {
  id: string;
  name: string;
  category: PricebookCategory;
  vendor: string;
  cost: number;
  unit: string;
  /** Markup sobre el COSTO, en porcentaje. **`null` significa «sigue el dial del
   *  taller»**, y no es lo mismo que 0 —que significa «se cobra a costo»—.
   *
   *  La distinción es nueva y es la que faltaba: mientras el tipo fue `number`,
   *  el lector no podía separar un 60% que una persona tecleó de un número que
   *  salió de una fórmula, y por ahí entraron 110 filas con markups derivados
   *  de un margen (42,857% = el 30% de margen reescrito sobre el costo). Ahora
   *  el constructor escribe `null` cuando nadie decidió nada
   *  (components/EstimateCalculator.tsx, persistPricebookEdits) y la columna
   *  `markup_source` de la migración 20260809130000 registra la procedencia.
   *
   *  Quien lo PINTA tiene que contemplar el nulo; quien lo USA para cotizar
   *  debe caer al dial. */
  markupPercent: number | null;
  /** De dónde salió `markupPercent` (migración 20260810060209, decisión 4 del
   *  plan de precios v2):
   *    'dial'    → copia del dial de aquel momento. El lector la ignora.
   *    'user'    → lo tecleó una persona. Gana sobre el dial.
   *    'derived' → salió de una fórmula de MARGEN, no de una decisión. Se ignora.
   *
   *  `'derived'` es el sello de las 68 filas contaminadas: markups como
   *  42,857% que no son más que un margen del 30% reescrito sobre el costo.
   *  Ausente en filas anteriores a la migración ⇒ se trata como 'dial'. */
  markupSource?: 'dial' | 'user' | 'derived';
  lastUpdated: string;
  /** Schema v3+: links a per-workspace price to a global catalog_items row.
   *  When present, this is the join key for template `pricebookItemHint`
   *  resolution. Null on legacy rows (manual-only pricebook items). */
  catalogItemId?: string | null;
  /** 'user' = added manually by contractor; 'template' = auto-created when
   *  contractor first set a price on a template-dropped row. */
  source?: 'user' | 'template';
  /** Tracks AI dedup review status for the dedup panel. */
  dedupStatus?: 'pending' | 'reviewed_same' | 'reviewed_different';
  /** Free-text note captured in AddMaterialModal (B-3). Persisted to pricebook.notes. */
  notes?: string;
}

export type ProductionRateUnit = 'Sq. Ft.' | 'Cu. Yard' | 'Linear Ft.' | 'Count' | 'Zone' | 'Hour';

export interface ServiceTemplate {
  id: string;
  name: string;
  category: string; // NEW: Grouping (e.g., Concrete, Demo)
  description: string;
  rateUnit: ProductionRateUnit;
  productionRate: number;
  wastePercent?: number; // NEW: Default waste factor (e.g., 10%)
  linkedPricebookItemId?: string;
  tags?: string[];
}


// --- SERVICES & MAINTENANCE MODULE ---

export type ServiceFrequency = 'Weekly' | 'Bi-Weekly' | 'Monthly';

export interface ServiceContract {
  id: string;
  clientId: string;
  clientName: string;
  serviceAddress: string;
  frequency: ServiceFrequency;
  startDate: string;
  endDate?: string;
  status: 'Active' | 'Paused' | 'Cancelled';
  monthlyRate: number;
  visitRate: number;
  billingType: 'Monthly' | 'PerVisit';
  /** Day of the month payment is due, 1–31. Optional: contracts without one
   *  fall back to the workspace default (`settings.rules.serviceInvoiceDueDay`).
   *
   *  For **Monthly** contracts this is a day of the month BEING BILLED — the
   *  coverage is charged up front, so August service is due on August's due day.
   *  **PerVisit** cannot work that way: the amount is not known until the visits
   *  have happened, so those bill at period close and fall on the same day of
   *  the FOLLOWING month.
   *
   *  Days past the end of a short month are clamped: 31 in February bills on
   *  the 28th (29th in a leap year) rather than rolling into March. */
  paymentDueDay?: number;
  description: string;
  assignedCrewIds?: string[];
  preferredDayOfWeek?: string;
  /** Preferred time of day in 24h "HH:MM" format. Used as an anchor by the
   *  route optimizer — visits with a time keep their slot; visits without
   *  a time fill in by nearest-neighbor proximity. */
  preferredTime?: string;
  location?: { lat: number; lng: number };
  routeSequences?: Record<string, number>;
  services?: string[];
  estimatedDurationMinutes?: number;
  seasonalSchedule?: { month: number; active: boolean }[];
  lastServiceDate?: string;
  totalVisitsCompleted?: number;
}

/**
 * One calendar month of billing for a service contract.
 *
 * Mirrors ProjectInvoice's status vocabulary so "paid" means the same thing on
 * both sides of the business, but keyed by PERIOD: the contract plus the month
 * is the identity, which is what stops a month being billed twice.
 */
export interface ContractInvoice {
  id: string;
  contractId: string;
  /** Calendar month billed, 'YYYY-MM'. */
  period: string;
  issuedOn: string;
  dueOn: string;
  amount: number;
  status: 'Draft' | 'Sent' | 'Paid' | 'Overdue';
  /** Stamped when marked Paid, cleared when moved back out. */
  paidAt?: string;
  notes?: string;
}

export interface ServiceVisit {
  id: string;
  contractId: string;
  clientId: string;
  clientName: string;
  date: string;
  status: 'Scheduled' | 'En Route' | 'On Site' | 'Completed' | 'Skipped';
  assignedCrewIds?: string[];
  completedBy?: string;
  completedAt?: string;
  notes?: string;
  photoUrl?: string;
  sequenceIndex?: number;
  serviceAddress?: string;
  startedAt?: string;
  durationMinutes?: number;
  photoUrls?: string[];
  checklist?: { item: string; done: boolean }[];
  crewNotes?: string;
}

export interface Route {
  id: string;
  name: string;
  date: string;
  crewId: string;
  visits: ServiceVisit[];
  status: 'Draft' | 'Active' | 'Completed';
}

export type ServiceRequestType = 'Extra Service' | 'Issue Report' | 'Schedule Change' | 'Other';
export type ServiceRequestStatus = 'Pending' | 'Reviewed' | 'Scheduled' | 'Completed' | 'Declined';

export interface ServiceRequest {
  id: string;
  clientId: string;
  contractId?: string;
  requestType: ServiceRequestType;
  description: string;
  status: ServiceRequestStatus;
  photoUrls?: string[];
  createdAt?: string;
  updatedAt?: string;
}

// --- COMPANY CAMERA TYPES ---

export interface ProjectImage {
  id: string;
  projectId: string;
  projectName: string;
  uploaderName: string;
  uploaderEmail: string;
  uploadDate: string; // ISO String
  url: string;
  path: string; // Storage path reference
}

// --- EQUIPMENT & FLEET TYPES ---

export interface MaintenanceLog {
  id: string;
  equipmentId: string;
  date: string;
  type: 'Oil Change' | 'Repair' | 'Inspection' | 'Other';
  description: string;
  cost: number;
  performedBy: string;
}

export type EquipmentStatus = 'Available' | 'On Job' | 'In Shop';

export interface Equipment {
  id: string;
  name: string;
  type: string; // e.g., 'Skid Steer', 'Mower', 'Truck'
  status: EquipmentStatus;
  location: string; // Can be 'Shop' or a Project Name/ID
  nextServiceDate?: string;
  notes?: string;
  /** Project it is currently signed out to. Explicitly `null` when returned —
   *  `undefined` is dropped by JSON.stringify, so the column would never clear. */
  projectId?: string | null;
}
